import React, { useState } from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Image from '@Components/Image';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { convertWeightKGtoLB } from '../../utils/convertWeight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';

interface Destination {
  name: string;
  flagURI: string;
  totalShipment: number;
  totalRevenue: number;
  totalWeight: number;
  shipmentPercentage: number;
  weightPercentage: number;
  revenuePercentage: number;
  _id: string;
}

interface Props {
  data: Destination[];
  setDestinationShipmentSort?: any;
  destinationRevenueSort: boolean;
  destinationWeightSort: boolean;
  destinationShipmentSort: boolean;
  setDestinationWeightSort?: any;
  setDestinationRevenueSort?: any;
}

const TopTenDestinationsTable: React.FC<Props> = ({
  data,
  setDestinationShipmentSort,
  setDestinationWeightSort,
  setDestinationRevenueSort,
  destinationShipmentSort,
  destinationWeightSort,
  destinationRevenueSort,
}) => {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const handleShipmentSort = () => {
    setDestinationShipmentSort((prev: any) => !prev);
    setDestinationWeightSort(false);
    setDestinationRevenueSort(false);
  };

  const handleWeightSort = () => {
    setDestinationWeightSort((prev: any) => !prev);
    setDestinationShipmentSort(false);
    setDestinationRevenueSort(false);
  };

  const handleRevenueSort = () => {
    setDestinationRevenueSort((prev: any) => !prev);
    setDestinationWeightSort(false);
    setDestinationShipmentSort(false);
  };

  const locale = useLocale();

  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const getPercentageColor = (percentage: number) => {
    if (percentage >= 10) return { bg: '#E8F5E9', text: '#2E7D32' };
    if (percentage >= 0) return { bg: '#E3F2FD', text: '#1976D2' };
    if (percentage >= -10) return { bg: '#FFF3E0', text: '#F57C00' };
    return { bg: '#FFEBEE', text: '#C62828' };
  };

  const MetricCard: React.FC<{
    value: number | string;
    percentage: number;
  }> = ({ value, percentage }) => {
    const colors = getPercentageColor(percentage);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#181D25',
            fontFamily: 'Poppins',
          }}
        >
          {value}
        </Typography>
        <Chip
          icon={percentage >= 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
          label={`${formatNumber(Math.abs(percentage), locale)}%`}
          size="small"
          sx={{
            height: '24px',
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: 'Poppins',
            bgcolor: colors.bg,
            color: colors.text,
            border: 'none',
            '& .MuiChip-icon': {
              color: colors.text,
              marginLeft: '6px',
            },
          }}
        />
      </Box>
    );
  };

  const SortableHeader: React.FC<{
    label: string;
    active: boolean;
    onClick: () => void;
  }> = ({ label, active, onClick }) => (
    <TableCell
      onClick={onClick}
      sx={{
        fontFamily: 'Poppins',
        fontWeight: active ? 600 : 500,
        fontSize: '14px',
        color: active ? '#1976D2' : '#606E80',
        cursor: 'pointer',
        textAlign: 'center',
        borderBottom: '2px solid #E0E0E0',
        bgcolor: active ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
        transition: 'all 0.2s',
        userSelect: 'none',
        '&:hover': {
          bgcolor: 'rgba(25, 118, 210, 0.08)',
          color: '#1976D2',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {label}
        <SwapVertIcon
          sx={{
            fontSize: 18,
            opacity: active ? 1 : 0.4,
            transition: 'opacity 0.2s',
          }}
        />
      </Box>
    </TableCell>
  );

  return (
    <Box sx={{ padding: 0 }}>
      {/* Main Table */}
      <TableContainer
        sx={{
          border: 'none',
          width: '100%',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        component={Paper}
      >
        <Table sx={{ border: 'none' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FA' }}>
              <TableCell
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#181D25',
                  borderBottom: '2px solid #E0E0E0',
                  minWidth: '220px',
                }}
              >
                Destination
              </TableCell>
              <SortableHeader label="Shipments" active={destinationShipmentSort} onClick={handleShipmentSort} />
              <SortableHeader
                label={`Weight ${name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`}
                active={destinationWeightSort}
                onClick={handleWeightSort}
              />
              <SortableHeader
                label={`Revenue (${defaultOrgCountryCurrency})`}
                active={destinationRevenueSort}
                onClick={handleRevenueSort}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((destination: Destination) => {
              return (
                <TableRow
                  key={destination._id}
                  onMouseEnter={() => setHoveredRow(destination._id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    transition: 'all 0.2s',
                    bgcolor: hoveredRow === destination._id ? '#F8F9FA' : 'transparent',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transform: 'scale(1.01)',
                    },
                    borderLeft: hoveredRow === destination._id ? '3px solid #1976D2' : '3px solid transparent',
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: 'Poppins',
                      borderBottom: '1px solid #F0F0F0',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Flag */}
                      <Box
                        sx={{
                          width: 40,
                          height: 28,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #E0E0E0',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#F5F5F5',
                        }}
                      >
                        <Image
                          src={destination.flagURI}
                          alt={`${destination.name} flag`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>

                      {/* Country Name */}
                      <Tooltip title={destination.name} arrow placement="top">
                        <Typography
                          sx={{
                            fontSize: '15px',
                            fontWeight: 500,
                            color: '#181D25',
                            fontFamily: 'Poppins',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                          }}
                        >
                          {destination.name}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
                    <MetricCard
                      value={formatNumber(destination.totalShipment, locale)}
                      percentage={destination.shipmentPercentage}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
                    <MetricCard
                      value={
                        name === 'Cargo Solutions Network'
                          ? formatNumber(convertWeightKGtoLB(destination.totalWeight), locale)
                          : formatNumber(destination.totalWeight, locale)
                      }
                      percentage={destination.weightPercentage}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
                    <MetricCard
                      value={formatNumber(destination.totalRevenue, locale)}
                      percentage={destination.revenuePercentage}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TopTenDestinationsTable;
